<script setup lang="ts">
import { DeliveryMethods } from '@/api/modules/basket/types'
import { Cost } from '@/types/api/api-v3/basket'
import { BasketPoint } from '@/types/api/api-v3/basket-points'
import { isAddressClient } from '@/types/store/basket'

import AddressSystemListShadow from '@/components/AddressSystem/AddressSystemListShadow.vue'
import BasketAddressesRadioGroup from '@/components/Basket/basket-addresses/BasketAddressesRadioGroup.vue'

import type { AddressClient } from '@/api/modules/addresses/addresses.types'

type Props = {
  maxCountOfVisibleAddresses: number
  addresses: AddressClient[] | BasketPoint[]
  modelValue?: AddressClient | BasketPoint
  deliveryCost?: Cost | null
  deliveryMethod: DeliveryMethods
  effectShadow?: boolean
  listWithAnimation?: boolean
}
const props = defineProps<Props>()

const emit = defineEmits([
  'update:model-value',
  'open-dialog-notification',
  'open-edit-address-dialog',
  'open-delete-address-dialog',
  'open-dialog-redirect-city',
])

const showAllAddresses = ref(false)

const showAllButtonVisible = computed(
  () => props.addresses.length > props.maxCountOfVisibleAddresses,
)

watch(
  () => props.addresses,
  (addresses) => {
    if (!props.modelValue || addresses.length === 0) return

    const valueIsNotIncludedInAddresses = addresses.every(
      ({ id }) => id !== props.modelValue?.id,
    )

    if (valueIsNotIncludedInAddresses) {
      change(null)
    }

    const valueHidden = props.addresses
      .slice(0, props.maxCountOfVisibleAddresses)
      .every(({ id }) => id !== props.modelValue?.id)

    if (!showAllAddresses.value && props.modelValue && valueHidden)
      showAllAddresses.value = true
  },
  {
    immediate: true,
  },
)

const change = (address: AddressClient | BasketPoint | null) => {
  // проверяем является ли это адрес и находится в зоне доставки
  if (!address || (isAddressClient(address) && !address.is_in_delivery_area))
    return

  // Если адрес имеет поле домена и value другого города
  const isAllowedAddress =
    'city_domain_prefix' in address && address?.allow_redirect

  // В случае, если адрес другого города
  if (isAllowedAddress) return

  emit('update:model-value', address)
}

const toggleShowAllAddresses = () => {
  showAllAddresses.value = !showAllAddresses.value
}

// Открываем блок уведомления по клику, если адрес не активен
const handleNotDeliveryAddress = (data: {
  event: Event
  address: AddressClient | BasketPoint
}) => {
  // В случае если выбран адрес вне зоны доставки
  if (isAddressClient(data.address) && !data.address.is_in_delivery_area) {
    emit('open-dialog-notification')
    data.event.preventDefault()

    return
  }

  // В случае если выбран адрес другого города
  if ('city_domain_prefix' in data.address && data.address.allow_redirect) {
    emit('open-dialog-redirect-city', data.address)
    data.event.preventDefault()
  }
}

const openEditAddressDialog = (address: AddressClient) => {
  emit('open-edit-address-dialog', address)
}

const openDeleteAddressDialog = (address: AddressClient) => {
  emit('open-delete-address-dialog', address)
}
</script>

<template>
  <div class="basket-addresses">
    <address-system-list-shadow
      v-if="addresses.length"
      parent-class="app-radio-group"
      :effect-shadow="effectShadow"
    >
      <basket-addresses-radio-group
        :max-count-of-visible-addresses="maxCountOfVisibleAddresses"
        :addresses="addresses"
        :show-all-addresses="showAllAddresses"
        :delivery-method="deliveryMethod"
        :active-address="modelValue"
        :delivery-cost="deliveryCost"
        :effect-shadow="effectShadow"
        :list-with-animation="listWithAnimation"
        @change="change"
        @click-radio="handleNotDeliveryAddress"
        @open-edit-address-dialog="openEditAddressDialog"
        @open-delete-address-dialog="openDeleteAddressDialog"
      />
    </address-system-list-shadow>

    <the-button
      v-if="showAllButtonVisible"
      class="basket-addresses__visibility-addresses"
      :class="{
        'basket-addresses__visibility-addresses--rotate': showAllAddresses,
      }"
      variant="tertiary"
      icon="expand-down"
      indent-left
      font="sm"
      size="sm"
      :ripple="false"
      @click.prevent="toggleShowAllAddresses"
    >
      <span v-if="showAllAddresses">Свернуть</span>
      <span v-else>Показать все адреса</span>
    </the-button>
  </div>
</template>

<style lang="scss" scoped>
.basket-addresses {
  height: inherit;
  &__visibility-addresses {
    margin-top: 20px;

    :deep(.app-icon) {
      transition: transform 0.3s ease;
    }

    &--rotate :deep(.app-icon) {
      transform: rotate(-180deg);
    }
  }
}
</style>
